"use client";

import React, { useEffect, useRef, useState } from "react";
import { Target, Zap, ShieldCheck, Globe, Award } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const milestones = [{
  year: "2026/1",
  title: "憬芯醫療正式成立",
  description: "公司於紅磡創立，確立以「居家醫護」為核心的服務方向，致力為香港家庭提供專業的醫療支援。",
  icon: Target
}, {
  year: "2026/2",
  title: "專業團隊全面擴充",
  description: "引入物理治療師、職業治療師及言語治療師，建立多元化的專職醫療團隊，提供全方位復康計劃。",
  icon: Globe
}, {
  year: "2026/2",
  title: "安心居家支援計劃",
  description: "推出特別支援服務，為長者及行動不便者提供到戶護理與健康監測，守護社區健康。",
  icon: ShieldCheck
}, {
  year: "2026/2",
  title: "服務卓越肯定",
  description: "服務範圍擴展至九龍及新界多區，累計服務超過百名居家使用者。",
  icon: Award
}, {
  year: "2026/3",
  title: "憬於未來 芯繫健康",
  description: "品牌形象全面升級，深化與社區資源聯繫，持續推動個人化復康方案，提升居家生活質素。",
  icon: Zap
}];
function MilestoneItem({
  item,
  index,
  isLeft
}: {
  item: (typeof milestones)[0];
  index: number;
  isLeft: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5,
    once: false
  });
  const Icon = item.icon;
  return <div data-section-id="121657" data-index={index} ref={ref} className={cn("relative flex flex-col md:flex-row items-start md:items-center w-full mb-16 last:mb-0 group", isLeft ? "md:flex-row-reverse" : "")}>
      {}
      <div className={cn("absolute left-6 md:left-1/2 w-5 h-5 rounded-full border-4 border-background -translate-x-1/2 transition-all duration-700 z-10 flex items-center justify-center", isVisible ? "bg-primary scale-125 shadow-[0_0_20px_rgba(124,58,237,0.4)]" : "bg-muted-foreground/30 scale-100")}>
        {isVisible && <div className="absolute w-full h-full rounded-full bg-primary/40 animate-ping" />}
      </div>

      {}
      <div className={cn("w-full pl-16 md:pl-0 md:w-[calc(50%-3rem)] transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", isLeft ? "md:pr-12 md:text-right" : "md:pl-12 md:text-left")}>
        <Card className={cn("relative overflow-hidden border-border/50 bg-card/90 backdrop-blur-md transition-all duration-500 rounded-2xl", isVisible ? "border-primary/20 shadow-xl shadow-primary/5" : "")}>
          {}
          <div className={cn("absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 transition-opacity duration-500", isVisible && "opacity-100")} />

          <CardHeader className="pb-2 relative z-10">
            <div className={cn("flex items-center gap-3 mb-2", isLeft ? "md:justify-end" : "justify-start")}>
              <div className={cn("p-2 rounded-xl transition-colors duration-500", isVisible ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
                <Icon className="w-5 h-5" />
              </div>
              <span className={cn("text-3xl font-bold tracking-tighter transition-colors duration-500 font-sans", isVisible ? "text-primary" : "text-muted-foreground/50")}>
                {item.year}
              </span>
            </div>
            <CardTitle className="text-xl font-medium text-foreground tracking-tight font-heading">
              {item.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <CardDescription className="text-base leading-relaxed text-muted-foreground font-medium">
              {item.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="hidden md:block md:w-[calc(50%-3rem)]" />
    </div>;
}
export default function DevelopmentMilestones() {
  return <section id="發展里程碑" className="py-24 lg:py-32 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] opacity-10 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%)] blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-sm font-bold tracking-widest text-primary uppercase mb-4 font-heading">
            Development Milestones
          </h2>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-medium text-foreground tracking-tight mb-6 font-hero italic">
            專業傳承，芯繫健康
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed">
            回顧憬芯醫療的成長之路，我們始終秉持專業與熱忱，將每一次的技術突破與服務升級，轉化為守護每一位服務使用者的堅實力量。
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-6 md:left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-primary/30 to-transparent md:-translate-x-1/2" />

          {}
          <div className="relative">
            {milestones.map((item, index) => <MilestoneItem key={index} item={item} index={index} isLeft={index % 2 === 0} />)}
          </div>
        </div>
      </div>
    </section>;
}